import { useQuery } from '@tanstack/react-query';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { testDataService, type TestDataEntityType } from '@/services/platform/test-data';

/**
 * Page-level helper to know which entities are currently flagged as test,
 * so lists/details can show a "Test" badge and toggle Mark/Unmark. Shares
 * the ['test-data','marked'] query (invalidated on every mark/unmark), so
 * badges update immediately. Gated by the test-data-tools flag.
 */
export function useTestMarks() {
  const me = useCurrentUser();
  const enabled = (me?.featureFlags ?? []).includes('test-data-tools');

  const query = useQuery({
    queryKey: ['test-data', 'marked'],
    queryFn: testDataService.listMarked,
    enabled,
  });

  const byType = new Map<TestDataEntityType, Set<string>>();
  for (const m of query.data ?? []) {
    let set = byType.get(m.entityType);
    if (!set) byType.set(m.entityType, (set = new Set()));
    set.add(m.entityId);
  }

  const isMarked = (entityType: TestDataEntityType, entityId: string) =>
    byType.get(entityType)?.has(entityId) ?? false;

  return { enabled, isMarked };
}
